import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  IconButton,
  Slide,
  CircularProgress,
  Divider,
  Paper,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  CalendarMonth as CalendarIcon,
  Security as SecurityIcon,
  ArrowCircleRight as ArrowRightIcon,
  ArrowCircleLeft as ArrowLeftIcon,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { useUpdateOnboardingStep } from '@features/user';

// Slide transition for the dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface GoogleCalendarOnboardingProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (proceed: boolean) => void;
}

interface StepContent {
  title: string;
  content: React.ReactNode;
}

const GoogleCalendarOnboarding: React.FC<GoogleCalendarOnboardingProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [checking, setChecking] = useState(false);
  const updateOnboardingStep = useUpdateOnboardingStep();

  // Mark this onboarding as complete so it doesn't show again
  const registerOnboardingComplete = async () => {
    setChecking(true);
    try {
      // Update onboarding steps to mark this as complete
      updateOnboardingStep('google_calendar_onboarding_completed', true);
      return true;
    } catch (error) {
      console.error('Error registering onboarding completion:', error);
      return false;
    } finally {
      setChecking(false);
    }
  };

  // Calendar Sync Diagram Component
  const SyncDiagram = () => (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        bgcolor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        my: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          py: 2,
        }}
      >
        {/* Left side - JotPsych */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '40%',
            p: 2,
            bgcolor: '#e3f2fd',
            borderRadius: '8px',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            JotPsych Calendar
          </Typography>
          <CalendarIcon sx={{ fontSize: 48, color: 'primary.main', my: 1 }} />
          <Typography variant="body2" align="center">
            Complete appointment data
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center">
            Patient info, notes, etc. (full PHI)
          </Typography>
        </Box>

        {/* Flow arrows */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ArrowRightIcon sx={{ color: 'success.main', fontSize: 32 }} />
            <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
              Preferred
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <ArrowLeftIcon sx={{ color: 'warning.main', fontSize: 32 }} />
            <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
              Limited data
            </Typography>
          </Box>
        </Box>

        {/* Right side - Google Calendar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '40%',
            p: 2,
            bgcolor: '#f5f5f5',
            borderRadius: '8px',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Google Calendar
          </Typography>
          <img
            src="/new-feature-images/google_calendar.png"
            alt="Google Calendar Icon"
            style={{ width: 48, height: 48, margin: '8px 0' }}
          />
          <Typography variant="body2" align="center">
            Basic appointment data
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center">
            Time, title (limited PHI)
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  // Step content definitions
  const steps: StepContent[] = [
    // Welcome & HIPAA Notice step
    {
      title: 'Google Calendar Sync',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Connect Your Google Calendar
          </Typography>

          <Typography paragraph>
            Calendar sync allows you to see your JotPsych appointments in your Google Calendar,
            making it easier to manage your schedule across platforms.
          </Typography>

          <Alert severity="info" icon={<SecurityIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Important Privacy Notice
            </Typography>
            <Typography variant="body2">
              By enabling syncing between JotPsych and Google Calendar, you acknowledge and agree to
              follow HIPAA compliance requirements and protect calendar information.
            </Typography>
          </Alert>

          <Typography paragraph>When you connect your calendar:</Typography>

          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              Your JotPsych appointments will appear in your Google Calendar
            </Typography>
            <Typography component="li" paragraph>
              We limit the patient information shared with Google Calendar to maintain privacy
            </Typography>
            <Typography component="li" paragraph>
              You'll need to authorize JotPsych to access your Google Calendar
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            <InfoIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
            You can disconnect calendar sync at any time in your account settings.
          </Typography>
        </Box>
      ),
    },

    // Data Flow Explanation step
    {
      title: 'How Sync Works',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Calendar Sync Flow
          </Typography>

          <Typography paragraph>
            For the best experience, we recommend creating and managing appointments in JotPsych
            first.
          </Typography>

          <SyncDiagram />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="success.main" gutterBottom>
              <ArrowRightIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              JotPsych → Google Calendar (Recommended)
            </Typography>
            <Typography variant="body2" paragraph sx={{ pl: 4 }}>
              Appointments created in JotPsych will sync to Google Calendar with all the essential
              information you need, including appointment type and basic details.
            </Typography>

            <Typography variant="subtitle1" color="warning.main" gutterBottom>
              <ArrowLeftIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Google Calendar → JotPsych (Limited)
            </Typography>
            <Typography variant="body2" paragraph sx={{ pl: 4 }}>
              While events from Google Calendar will appear in JotPsych, they won't contain the
              specific metadata needed for complete clinical workflows.
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Always create the appointment in JotPsych to ensure you have proper clinical context.
            </Typography>
          </Alert>
        </Box>
      ),
    },

    // Final step - Ready to Connect
    {
      title: 'Ready to Connect',
      content: (
        <Box>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              You're ready to connect!
            </Typography>
          </Box>

          <Typography paragraph>When you click "Connect Calendar" below:</Typography>

          <Box component="ol" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              You'll be prompted to sign in to your Google account
            </Typography>
            <Typography component="li" paragraph>
              You'll need to authorize JotPsych to access your calendar
            </Typography>
            <Typography component="li" paragraph>
              Once authorized, your calendar sync will begin immediately
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: '4px', mb: 3 }}>
            <Typography variant="body2" color="primary.dark">
              <InfoIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              You can manage or disconnect this integration at any time in your account settings.
            </Typography>
          </Paper>

          <Typography paragraph align="center">
            Ready to sync your calendars?
          </Typography>
        </Box>
      ),
    },
  ];

  const handleNext = async () => {
    // Register onboarding complete when moving to final step
    if (activeStep === steps.length - 2) {
      setChecking(true);
      const registrationSuccess = await registerOnboardingComplete();
      setChecking(false);

      if (!registrationSuccess) {
        console.error('Failed to register onboarding completion');
      }
    }

    if (activeStep < steps.length - 1) {
      setActiveStep(prevStep => prevStep + 1);
    } else {
      // Call onComplete callback with true to indicate proceeding with calendar sync
      if (onComplete) {
        onComplete(true);
      }
      onClose();
    }
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleCancel = () => {
    // Call onComplete with false to indicate user canceled
    if (onComplete) {
      onComplete(false);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: 'primary.light',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Google Calendar Sync Setup</Typography>
        </Box>
        <IconButton
          edge="end"
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{ color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, pt: 2 }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {steps[activeStep].content}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        {activeStep === 0 ? (
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
        ) : (
          <Button onClick={handleBack} disabled={checking} startIcon={<ArrowBackIcon />}>
            Back
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={checking}
          endIcon={
            checking ? (
              <CircularProgress size={16} color="inherit" />
            ) : activeStep === steps.length - 1 ? undefined : (
              <ArrowForwardIcon />
            )
          }
          color={activeStep === steps.length - 1 ? 'success' : 'primary'}
        >
          {activeStep === steps.length - 1 ? 'Connect Calendar' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleCalendarOnboarding;
